import { useState, useEffect, useRef } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const PRESETS = [
  { id: "today", label: "Today", days: 0 },
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
  { id: "mtd", label: "This month", custom: "mtd" },
  { id: "qtd", label: "This quarter", custom: "qtd" },
  { id: "fy", label: "Fiscal year to date", custom: "fy" },
  { id: "ytd", label: "This year", custom: "ytd" },
  { id: "all", label: "All time", days: null },
];

function toISO(d) {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function fmt(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function presetRange(id) {
  const today = new Date();
  const end = new Date(today);
  let start = new Date(today);
  if (id === "today") {
    // start = today
  } else if (id === "mtd") {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
  } else if (id === "qtd") {
    const q = Math.floor(today.getMonth() / 3);
    start = new Date(today.getFullYear(), q * 3, 1);
  } else if (id === "fy") {
    // Australian Fiscal Year: 1 Jul → 30 Jun. Current FY starts on 1 Jul of
    // (this year) if we're past June, else 1 Jul of (last year).
    const y = today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
    start = new Date(y, 6, 1);
  } else if (id === "ytd") {
    start = new Date(today.getFullYear(), 0, 1);
  } else if (id === "all") {
    return { from: "", to: "" };
  } else {
    const days = PRESETS.find((p) => p.id === id)?.days || 0;
    start.setDate(today.getDate() - days);
  }
  return { from: toISO(start), to: toISO(end) };
}

export default function DateRangePicker({ value, onChange, defaultPreset = "30d" }) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ from: null, to: null });
  const initRef = useRef(false);

  // Initialize: if no value provided, set default preset
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    if (!value || (!value.from && !value.to)) {
      const r = presetRange(defaultPreset);
      onChange?.(r);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setRange({ from: value?.from ? new Date(value.from) : null, to: value?.to ? new Date(value.to) : null });
  }, [value?.from, value?.to]);

  const applyPreset = (id) => {
    const r = presetRange(id);
    onChange?.(r);
    setOpen(false);
  };

  const onSelectCalendar = (r) => {
    setRange(r || { from: null, to: null });
    if (r?.from && r?.to) {
      onChange?.({ from: toISO(r.from), to: toISO(r.to) });
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.({ from: "", to: "" });
  };

  const labelText =
    value?.from && value?.to ? `${fmt(value.from)} → ${fmt(value.to)}`
    : value?.from ? `From ${fmt(value.from)}`
    : value?.to ? `Until ${fmt(value.to)}`
    : "All time";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="date-range-picker"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
        >
          <CalendarIcon className="w-4 h-4 text-slate-500" />
          <span className="font-medium">{labelText}</span>
          {(value?.from || value?.to) && (
            <span onClick={clear} className="ml-1 p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" title="Clear">
              <X className="w-3 h-3" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0 z-50" data-testid="date-range-popover">
        <div className="flex">
          <div className="w-44 border-r border-slate-100 p-2 bg-slate-50/40">
            <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-slate-500 px-2 py-1.5">Quick Select</div>
            {PRESETS.map((p) => (
              <button
                key={p.id}
                data-testid={`preset-${p.id}`}
                onClick={() => applyPreset(p.id)}
                className="w-full text-left px-2 py-1.5 rounded text-sm text-slate-700 hover:bg-white hover:text-slate-900"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="p-2">
            <Calendar
              mode="range"
              selected={range}
              onSelect={onSelectCalendar}
              numberOfMonths={2}
              defaultMonth={range.from || new Date()}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
